import { Injectable } from '@angular/core';
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { ModalController } from '@ionic/angular/standalone';
import { environment } from '../../environments/environment';
import { AppUpdatesComponent } from '../shared/app-updates/app-updates.component';
import { TranslateService } from '@ngx-translate/core';
import { App } from '@capacitor/app';

@Injectable({ providedIn: 'root' })
export class UpdateService {
  constructor(
    private readonly modalController: ModalController,
    private readonly translateService: TranslateService,
  ) {}

  async init(): Promise<void> {
    try {
      const promises: Promise<any>[] = [];
      promises.push(this.getMaintenanceInfo());
      if (Capacitor.getPlatform() !== 'web') {
        // This check is not applicable for the web
        promises.push(this.getAvailableAppInfo());
      }
      const [maintenance, available] = await Promise.all(promises);
      if (maintenance) await this.presentModal(maintenance);
      if (available) await this.presentModal(available);
    } catch {
      return;
    }
  }

  async openAppStore(): Promise<void> {
    return AppUpdate.openAppStore();
  }

  private async getCurrentAppVersion(): Promise<number> {
    try {
      const result = await AppUpdate.getAppUpdateInfo();
      if (Capacitor.getPlatform() === 'android') {
        return Number(result.currentVersionCode);
      } else {
        return Number(result.currentVersionName.replaceAll('.', ''));
      }
    } catch {
      const result = await App.getInfo();
      if (Capacitor.getPlatform() === 'android') {
        return Number(result.build);
      } else {
        return Number(result.version.replaceAll('.', ''));
      }
    }
  }

  private async getAvailableAppInfo(): Promise<any> {
    const res = await this.getContent(environment.cmsVersionId);
    const current = await this.getCurrentAppVersion();
    const version = Number((Capacitor.getPlatform() === 'android' ? res['android'] : res['iOS']).replaceAll('.', ''));
    if (current >= version) {
      return undefined;
    }
    return res;
  }

  private getMaintenanceInfo(): Promise<any> {
    return this.getContent(environment.cmsMaintenanceId);
  }

  private async presentModal(body: any): Promise<any> {
    const modal = await this.modalController.create({
      component: AppUpdatesComponent,
      componentProps: body,
      canDismiss: async (data) => {
        if (body.canDismiss) return true;
        return data?.source === 'action';
      },
      handle: body.canDismiss,
    });
    await modal.present();
    return modal.onDidDismiss();
  }

  private async getContent(contentId: number): Promise<any> {
    try {
      const locale = this.translateService.currentLang || this.translateService.defaultLang || 'en';
      const response = await CapacitorHttp.get({
        url: `${environment.cmsUrl}/api/patient-app-config/${contentId}?locale=${locale}`,
      });
      const { data, status } = response;
      if (status >= 400 || !data) {
        return null;
      }
      return data?.value || null;
    } catch {
      return null;
    }
  }
}
